import { HStack, IconButton } from '@chakra-ui/react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import StatusCellRenderer from '../cellRenderer/StatusCellRenderer';
import PlatformCellRenderer from 'components/tables/AGGrid/CustomCells/PlatformCellRenderer';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import TimeCellRenderer from 'components/tables/AGGrid/CustomCells/TimeCellRenderer';
import NumberCellRenderer from '../cellRenderer/NumberCellRenderer';
import ScopeType from 'utils/enums/ScopeType';
import Platforms from 'utils/platforms';
import Statuses from 'pages/telephony/numbers/utils/statuses';
import Tooltip from 'components/v4/Tooltip';
import { Text } from 'components/v4';

import { renderTelephoneNumber } from 'utils/utils';

export const getColDefs = (
    countriesArray,
    currentScope,
    darkMode,
    handlePlatformEditBtn,
) => [
    {
        maxWidth: 70,
        width: 70,
        filter: false,
        suppressMovable: true,
        lockVisible: true,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        enableRowGroup: false,
        resizable: false,
        sortable: false,
        cellRenderer: 'agGroupCellRenderer',
        lockPosition: 'left',
    },
    {
        field: 'telephoneNumber',
        headerName: 'Number',
        cellClass: 'main-number-cell',
        enableRowGroup: false,
        width: 200,
        flex: 0.5,
        cellRenderer: (params) => {
            return (
                <NumberCellRenderer
                    value={params.value}
                    valueFormatted={renderTelephoneNumber(
                        params.data,
                        'telephoneNumber',
                    )}
                    countries={countriesArray}
                    params={params}
                />
            );
        },
        useValueFormatterForExport: false,
    },
    // (2924) Status and Trunk both describe a number's company-level trunk
    // assignment, which the Partner-scoped payload does not carry — they are
    // dropped from the table at Partner scope and above.
    ...(currentScope >= ScopeType.PARTNER
        ? []
        : [makeNumberStatusCol(), makeTrunkNameCol(false)]),
    makeCliPresentationCol({ hide: false, useValueFormatterForExport: false }),
    {
        field: 'trsRegistrarName',
        headerName: 'TRS Registrar',
        hide: true,
        enableRowGroup: false,
        cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
        field: 'trsExtension',
        headerName: 'TRS Extension',
        hide: true,
        enableRowGroup: false,
        cellStyle: { display: 'flex', alignItems: 'center' },
    },
    makeAttribute1Col(false),
    makeAttribute2Col(false),
    makeAttribute3Col(false),
    makeOrganisationCol('wholesaler', currentScope < ScopeType.GLOBAL),
    makeOrganisationCol('partner', currentScope < ScopeType.WHOLESALER),
    makeOrganisationCol('company', currentScope < ScopeType.PARTNER),
    makeActionCol(darkMode, handlePlatformEditBtn, [true]),
];

// ─── Shared helpers ───────────────────────────────────────────────────────────

export const statusFilterCellRenderer = (params) => {
    const isSelectedAll = params?.value === '(Select All)';
    const statusLookup = Statuses[params?.value];
    const displayName = statusLookup?.displayedName;
    const additionalInfo = statusLookup?.toolTip
        ? `(${statusLookup.toolTip})`
        : '';
    if (!isSelectedAll) {
        return <Text isTruncated>{`${displayName} ${additionalInfo}`}</Text>;
    }
    return '(Select All)';
};
statusFilterCellRenderer.displayName = 'StatusFilterCellRenderer';

// ─── Shared column factories ──────────────────────────────────────────────────

const makeTelephoneNumberCol = (hasPlatformSelected, countriesArray) => ({
    field: 'telephoneNumber',
    headerName: 'Number',
    cellClass: 'platform-number-cell',
    enableRowGroup: hasPlatformSelected ? true : false,
    width: 200,
    flex: 1.2,
    cellRenderer: (params) => (
        <NumberCellRenderer
            value={params.value}
            valueFormatted={renderTelephoneNumber(
                params.data,
                'telephoneNumber',
            )}
            countries={countriesArray}
            params={params}
        />
    ),
    cellRendererParams: {
        suppressPadding: false,
        innerRenderer: CopyCellRenderer,
    },
    useValueFormatterForExport: false,
});

const makePlatformStatusCol = (hasPlatformSelected) => ({
    field: 'platformStatus',
    headerName: 'Status',
    enableRowGroup: hasPlatformSelected ? true : false,
    cellRenderer: (params) => {
        const isDisabled =
            params.data?.platformStatus === 0 && params.data?.trunkID == null
                ? 'disabled'
                : params.value;
        return (
            <StatusCellRenderer
                value={isDisabled}
                statusMessage={params.data.statusMessage}
                operatorStatus={params.data.operatorStatus}
                operatorMessage={params.data.operatorMessage}
            />
        );
    },
    cellStyle: { display: 'flex', alignItems: 'center' },
    filter: 'agSetColumnFilter',
    valueFormatter: (params) => {
        const statusLookup = Statuses[params?.value];
        return statusLookup?.displayedName;
    },
    filterValueGetter: (params) => {
        const isDisabled =
            params?.data.platformStatus === 0 && params?.data.trunkID == null;
        return isDisabled ? 'disabled' : params?.data?.platformStatus;
    },
    filterParams: { cellRenderer: statusFilterCellRenderer },
});

const makeNumberStatusCol = () => ({
    field: 'status',
    enableRowGroup: false,
    cellRenderer: (params) => {
        return (
            <StatusCellRenderer
                value={params.data?.status}
                statusMessage={params.data.engineMessage}
                operatorStatus={params.data.operatorStatus}
                operatorMessage={params.data.operatorMessage}
                trunkName={params.data?.trunkName}
                parent
            />
        );
    },
    cellStyle: { display: 'flex', alignItems: 'center' },
    filter: true,
    valueGetter: (params) => {
        const status = params?.data?.status;
        if (status === 0) {
            return params.data?.trunkName || 'Active';
        }
        const statusLookup = Statuses[status];
        return statusLookup?.displayedName;
    },
    valueFormatter: (params) => {
        if (params.value === 0) {
            return params.data?.trunkName || 'Active';
        }
        const statusLookup = Statuses[params?.value];
        return statusLookup?.displayedName;
    },
    filterValueGetter: (params) => {
        const isDisabled =
            params?.data?.status === 0 &&
            params?.data?.trunkName?.toLowerCase() === 'disabled';
        return isDisabled ? 'disabled' : params?.data?.status;
    },
    filterParams: { cellRenderer: statusFilterCellRenderer },
});

const makeTrunkNameCol = (hasPlatformSelected) => ({
    field: 'trunkName',
    headerName: 'Trunk',
    enableRowGroup: hasPlatformSelected ? true : false,
    cellRenderer: (params) => (
        <CopyCellRenderer tooltip={params.data?.trunk} {...params} />
    ),
});

const makeUserCol = (hasPlatformSelected) => ({
    field: 'user',
    enableRowGroup: hasPlatformSelected ? true : false,
});

const makeOverstampNumberCol = (hasPlatformSelected) => ({
    field: 'overstampNumber',
    headerName: 'Overstamp',
    enableRowGroup: hasPlatformSelected ? true : false,
    hide: true,
    valueFormatter: (params) => (params.value ? '+' + params.value : ''),
});

const makeLastModifiedTSCol = (hasPlatformSelected) => ({
    field: 'lastModifiedTS',
    headerName: 'Last Updated',
    enableRowGroup: hasPlatformSelected ? true : false,
    cellRenderer: TimeCellRenderer,
});

const makeCliPresentationCol = ({
    hide = true,
    useValueFormatterForExport,
} = {}) => ({
    field: 'cliPresentation',
    headerName: 'Caller ID',
    enableRowGroup: false,
    valueFormatter: (params) =>
        renderTelephoneNumber(params.data, 'cliPresentation'),
    ...(hide !== undefined && { hide }),
    ...(useValueFormatterForExport !== undefined && {
        useValueFormatterForExport,
    }),
});

// (2710) /numbers/summary names the owning organisations `wholesalerName` /
// `partnerName` / `companyName`, while other number payloads use the bare field
// name. Read whichever the row carries so these columns are never blank.
const makeOrganisationCol = (field, hide) => ({
    field,
    enableRowGroup: false,
    hide,
    cellStyle: { display: 'flex', alignItems: 'center' },
    valueGetter: (params) =>
        params.data?.[`${field}Name`] ?? params.data?.[field] ?? '',
});

const makeAttribute1Col = (enableRowGroup) => ({
    field: 'attribute1',
    headerName: 'Attribute 1',
    enableRowGroup,
    hide: true,
});

const makeAttribute2Col = (enableRowGroup) => ({
    field: 'attribute2',
    headerName: 'Attribute 2',
    enableRowGroup,
    hide: true,
});

const makeAttribute3Col = (enableRowGroup) => ({
    field: 'attribute3',
    headerName: 'Attribute 3',
    enableRowGroup,
    hide: true,
});

const makeNumberAttribute1Col = (hasPlatformSelected) => ({
    field: 'numberAttribute1',
    headerName: 'Number Attribute 1',
    enableRowGroup: hasPlatformSelected ? true : false,
    hide: true,
});

const makeNumberAttribute2Col = (hasPlatformSelected) => ({
    field: 'numberAttribute2',
    headerName: 'Number Attribute 2',
    enableRowGroup: hasPlatformSelected ? true : false,
    hide: true,
});

const makeNumberAttribute3Col = (hasPlatformSelected) => ({
    field: 'numberAttribute3',
    headerName: 'Number Attribute 3',
    enableRowGroup: hasPlatformSelected ? true : false,
    hide: true,
});

const makeActionCol = (darkMode, handlePlatformEditBtn, onClickArgs = []) => ({
    maxWidth: 100,
    headerName: 'Action',
    filter: false,
    suppressMovable: true,
    lockVisible: true,
    suppressHeaderMenuButton: true,
    suppressColumnsToolPanel: true,
    suppressHeaderFilterButton: true,
    enableRowGroup: false,
    resizable: false,
    sortable: false,
    lockPosition: 'right',
    cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
    cellRenderer: (params) => (
        <HStack
            spacing={0}
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%">
            <Tooltip label="Edit Number">
                <IconButton
                    icon={
                        <BsThreeDotsVertical
                            fill={darkMode ? 'white' : 'black'}
                        />
                    }
                    aria-label="Edit"
                    variant="ghost"
                    _hover={{
                        bg: darkMode ? 'whiteAlpha.200' : 'blackAlpha.100',
                    }}
                    _active={{
                        bg: darkMode ? 'whiteAlpha.300' : 'blackAlpha.200',
                    }}
                    _focus={{ boxShadow: 'none' }}
                    onClick={() =>
                        handlePlatformEditBtn(params, ...onClickArgs)
                    }
                />
            </Tooltip>
        </HStack>
    ),
});

// ─── Shared column helpers grouped by usage ───────────────────────────────────

// ─── Single platform view (no Platform column) ────────────────────────────────

export const getSinglePlatformNumberColDefs = (
    darkMode,
    handlePlatformEditBtn,
    hasPlatformSelected,
    countriesArray,
) => [
    makeTelephoneNumberCol(hasPlatformSelected, countriesArray),
    makePlatformStatusCol(hasPlatformSelected),
    makeTrunkNameCol(hasPlatformSelected),
    makeUserCol(hasPlatformSelected),
    makeOverstampNumberCol(hasPlatformSelected),
    makeLastModifiedTSCol(hasPlatformSelected),
    makeCliPresentationCol(),
    makeAttribute1Col(!!hasPlatformSelected),
    makeAttribute2Col(!!hasPlatformSelected),
    makeAttribute3Col(!!hasPlatformSelected),
    makeNumberAttribute1Col(hasPlatformSelected),
    makeNumberAttribute2Col(hasPlatformSelected),
    makeNumberAttribute3Col(hasPlatformSelected),
    makeActionCol(darkMode, handlePlatformEditBtn),
];

// ─── Detail grid view (includes Platform column) ─────────────────────────────

export const getPlatformNumberColDefs = (
    darkMode,
    handlePlatformEditBtn,
    hasPlatformSelected,
    countriesArray,
) => [
    {
        field: 'platformID',
        headerName: 'Platform',
        enableRowGroup: hasPlatformSelected ? true : false,
        width: 120,
        flex: 0.6,
        cellRenderer: PlatformCellRenderer,
        filter: 'agSetColumnFilter',
        filterParams: {
            valueFormatter: (params) => Platforms[params.value].name,
        },
        cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
    },
    makeTelephoneNumberCol(hasPlatformSelected, countriesArray),
    makePlatformStatusCol(hasPlatformSelected),
    makeTrunkNameCol(hasPlatformSelected),
    makeUserCol(hasPlatformSelected),
    makeOverstampNumberCol(hasPlatformSelected),
    makeLastModifiedTSCol(hasPlatformSelected),
    makeCliPresentationCol(),
    makeAttribute1Col(!!hasPlatformSelected),
    makeAttribute2Col(!!hasPlatformSelected),
    makeAttribute3Col(!!hasPlatformSelected),
    makeNumberAttribute1Col(hasPlatformSelected),
    makeNumberAttribute2Col(hasPlatformSelected),
    makeNumberAttribute3Col(hasPlatformSelected),
    makeActionCol(darkMode, handlePlatformEditBtn),
    //TODO: For Task #147
    // { field: 'wholesaler', hide: true, enableRowGroup: hasPlatformSelected ? true : false },
    // { field: 'partner', hide: true, enableRowGroup: hasPlatformSelected ? true : false },
    // { field: 'company', hide: true, enableRowGroup: hasPlatformSelected ? true : false },
    // { field: 'attributes', hide: true, enableRowGroup: hasPlatformSelected ? true : false },
];

export const defaultCellRendererColDef = {
    sortable: false,
    filter: false,
    resizable: true,
    flex: 1,
    minWidth: 100,
    suppressHeaderMenuButton: true,
    cellRenderer: CopyCellRenderer,
};

export const getDetailCellRendererParams = (
    platformNumberColDefs,
    defaultCellRendererColDef,
    setSelectedRows,
    onDetailRowExpand,
) => ({
    detailGridOptions: {
        rowSelection: {
            mode: 'multiRow',
            isRowSelectable: (params) =>
                params.data?.platformStatus === 7 ? true : false,
            hideDisabledCheckboxes: true,
            headerCheckbox: false,
        },
        onRowSelected: (params) => {
            const currentRow = params.data;

            setSelectedRows((prevSelectedRows) => {
                const isAlreadySelected = prevSelectedRows.some(
                    (row) => row.numberID === currentRow.numberID,
                );
                if (isAlreadySelected) {
                    return prevSelectedRows.filter(
                        (row) => row.numberID !== currentRow.numberID,
                    );
                } else {
                    return [...prevSelectedRows, currentRow];
                }
            });
        },
        pagination: false,
        columnDefs: platformNumberColDefs,
        headerHeight: 38,
        defaultColDef: defaultCellRendererColDef,
    },
    // Every expand re-arms the fetch, re-opening an already opened row
    // included: its platform numbers can change while the detail grid is
    // closed, so the previous response is not replayed.
    getDetailRowData: (params) => onDetailRowExpand(params),
});

export const getRowSelectionConfig = (
    hasPlatformSelected,
    selectedTableData,
) => {
    const hasActionRequiredNumber =
        hasPlatformSelected &&
        selectedTableData?.data?.some((row) => row.platformStatus === 7);

    return {
        mode: 'multiRow',
        checkboxes: hasActionRequiredNumber,
        isRowSelectable: (params) =>
            hasActionRequiredNumber && params.data?.platformStatus === 7,
        hideDisabledCheckboxes: true,
        headerCheckbox: false,
        masterSelects: 'detail',
    };
};

export const getQueryConfig = (
    currentCompany,
    selectedOption,
    currentScope,
    platformList,
) => {
    const platformId =
        platformList?.length === 1 ? platformList[0]?.id : selectedOption?.id;

    return {
        queryKey: [
            'getCompanyPlatformNumbers',
            currentCompany,
            platformId,
            currentScope,
        ],
        fetchFunctionParams: [currentCompany, platformId, currentScope],
        enabled:
            (platformList?.length === 1 && platformList[0]?.id !== undefined) ||
            (selectedOption !== undefined && selectedOption?.id !== undefined),
        staleTime: 0,
    };
};

export const getNumbersQueryConfig = (
    currentScope,
    currentWholesaler,
    currentPartner,
    currentCompany,
) => ({
    scope: currentScope,
    wholesaler: currentWholesaler,
    partner: currentPartner,
    company: currentCompany,
});

export const PLATFORM_STATUS = {
    OK: 0,
    ACTION_REQUIRED: 7,
    CONFIRMATION_REQUIRED: 17,
};

export const TABLE_REFRESH_THRESHOLD = 500;

export const MODAL_CONFIG = {
    confirmNumbers: {
        title: 'Confirm Transfer',
        description:
            'The following numbers have been detected as existing in the Teams Tenant and require confirmation to transfer.',
    },
};

export const WALKTHROUGH_IDS = {
    csvUpdate: '/numbers/button/csv-update',
    confirm: '/numbers/button/confirm',
    addNumbers: '/numbers/button/add',
};

export const createDataFetchWrapper = (setData, dataFetchedRef) => (data) => {
    setData(data);
    dataFetchedRef.current = true;
};

export const createPlatformDataFetchWrapper =
    (setPlatformsTableData, setSummaryData, platformNumbersDataFetchedRef) =>
    (data) => {
        setPlatformsTableData((prev) => ({
            ...prev,
            data: data.platformNumbers,
        }));
        setSummaryData((prev) => ({
            ...prev,
            platformNumbers: data.platformNumbers.length,
            activePlatformNumbers: data.activePlatformNumbers,
            disabledPlatformNumbers: data.disabledPlatformNumbers,
            pendingPlatformNumbers: data.pendingPlatformNumbers,
        }));
        platformNumbersDataFetchedRef.current = true;
    };

export const computeHasPlatformSelected = (selectedOption, platformList) =>
    selectedOption !== undefined || platformList?.length === 1;

// Whether the query feeding the table still has work outstanding.
// An undefined platformList means the list both numbers queries are gated on is
// still resolving, so neither has issued a request yet. Reporting that as
// settled ended a scope switch before its fetch even started, which is why the
// first Company -> Partner switch showed no loading while it loaded (2937).
export const isTableFetchInFlight = (
    platformList,
    isPlatformView,
    isLoading,
    isRefetching,
    isFetching,
    isLoadingCompanyPlatformNumbers,
    isRefetchingPlatformNumbers,
    isFetchingCompanyPlatformNumbers,
) =>
    platformList === undefined ||
    (isPlatformView
        ? isLoadingCompanyPlatformNumbers ||
          isRefetchingPlatformNumbers ||
          isFetchingCompanyPlatformNumbers
        : isLoading || isRefetching || isFetching);

// isPlatformView: true when a platform's numbers feed the table (either a single
// available platform or one picked from the filter), so the loading flags and the
// already-fetched marker are both read from the platform numbers query.
export const getLoadingState = (
    isPlatformView,
    numbersDataFetched,
    platformNumbersDataFetched,
    isLoading,
    isRefetching,
    isFetching,
    isLoadingCompanyPlatformNumbers,
    isRefetchingPlatformNumbers,
    isFetchingPlatformNumbers,
    gridRef,
    platformList,
) => {
    const hasData = isPlatformView
        ? platformNumbersDataFetched.current
        : numbersDataFetched.current;

    // If queries haven't been enabled yet, show loading
    const queryNotEnabledYet = platformList === undefined;

    return (
        (queryNotEnabledYet ||
            (!isPlatformView && (isLoading || isRefetching || isFetching)) ||
            (isPlatformView &&
                (isLoadingCompanyPlatformNumbers ||
                    isRefetchingPlatformNumbers ||
                    isFetchingPlatformNumbers)) ||
            !gridRef) &&
        !hasData
    );
};

export const sanitiseSinglePlatformState = (state) => {
    if (!state) return state;
    const stripPlatformID = (ids) => ids?.filter((id) => id !== 'platformID');
    return {
        ...state,
        ...(state.columnOrder && {
            columnOrder: {
                ...state.columnOrder,
                orderedColIds: stripPlatformID(state.columnOrder.orderedColIds),
            },
        }),
        ...(state.columnVisibility && {
            columnVisibility: {
                ...state.columnVisibility,
                hiddenColIds: stripPlatformID(
                    state.columnVisibility.hiddenColIds,
                ),
            },
        }),
        ...(state.columnSizing && {
            columnSizing: {
                ...state.columnSizing,
                columnSizingModel: state.columnSizing.columnSizingModel?.filter(
                    (c) => c.colId !== 'platformID',
                ),
            },
        }),
        ...(state.columnPinning && {
            columnPinning: {
                ...state.columnPinning,
                leftColIds: stripPlatformID(state.columnPinning.leftColIds),
                rightColIds: stripPlatformID(state.columnPinning.rightColIds),
            },
        }),
    };
};

export const sanitiseMasterDetailState = (state) => {
    if (!state) return state;
    return { ...state };
};
